import {
  CalcDefinition,
  CalcResult,
  ChemExperimentState,
  CalculationEngine,
  MaterialLine,
  CalcVariableDefinition,
} from './types';

/**
 * Calculation Engine - Phase 4
 * 
 * Evaluates calculations using real chemistry formulas.
 * Supports both legacy formula-based and new evaluator-based calculations.
 */

/**
 * Round helper function
 */
function round(value: number, decimals: number = 2): number {
  if (!isFinite(value)) return value;
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Phase 4: Resolve variables from various sources
 */
function resolveVariables(
  variables: CalcVariableDefinition[],
  experiment: ChemExperimentState,
  manualInputs: Record<string, number>
): Record<string, number> {
  const result: Record<string, number> = {};

  for (const v of variables) {
    switch (v.source.type) {
      case 'manual': {
        const val = manualInputs[v.name];
        if (val == null && v.required) {
          result[v.name] = NaN;
        } else {
          result[v.name] = val ?? 0;
        }
        break;
      }

      case 'constant': {
        result[v.name] = v.source.value;
        break;
      }

      case 'bomAggregate': {
        const { role, field, mode } = v.source;
        const relevant = experiment.materials.filter((m) => m.role === role);

        const values = relevant
          .map((m) => {
            if (field === 'quantity') return m.quantity;
            if (field === 'metadata.molWeight') {
              return m.metadata?.molWeight as number | undefined;
            }
            if (field === 'metadata.purity') {
              return m.metadata?.purity as number | undefined;
            }
            return undefined;
          })
          .filter((x): x is number => typeof x === 'number' && !isNaN(x));

        if (!values.length) {
          result[v.name] = NaN;
          break;
        }

        if (mode === 'first') {
          result[v.name] = values[0];
        } else if (mode === 'average') {
          const sum = values.reduce((a, b) => a + b, 0);
          result[v.name] = sum / values.length;
        } else {
          // sum
          result[v.name] = values.reduce((a, b) => a + b, 0);
        }
        break;
      }

      case 'assay': {
        const found = experiment.assays?.find((a) => a.basis === v.source.basis);
        result[v.name] = found?.value ?? NaN;
        break;
      }
    }
  }

  return result;
}

/**
 * Phase 4: Real API Evaluators
 */

function evalApiStoichYield(
  def: CalcDefinition,
  vars: Record<string, number>
): CalcResult {
  const limitingMol = vars['limitingMol'];
  const stoichRatio = vars['stoichRatio'] ?? 1;
  const productMW = vars['productMW'];
  const actualMass = vars['actualMass'];

  if (!limitingMol || !productMW || !actualMass || isNaN(limitingMol) || isNaN(productMW) || isNaN(actualMass)) {
    return { calcId: def.id, value: NaN, unit: def.outputUnit ?? '%' };
  }

  const theoreticalMass = limitingMol * stoichRatio * productMW;
  if (!theoreticalMass || theoreticalMass === 0) {
    return { calcId: def.id, value: NaN, unit: def.outputUnit ?? '%' };
  }

  const yieldPercent = (actualMass / theoreticalMass) * 100;
  return {
    calcId: def.id,
    value: round(yieldPercent),
    unit: def.outputUnit ?? '%',
    timestamp: new Date(),
  };
}

function evalApiGreenMetric(
  def: CalcDefinition,
  vars: Record<string, number>
): CalcResult {
  const massProducts = vars['massProducts'];
  const massAll = vars['massAllMaterials'];

  if (!massProducts || !massAll || isNaN(massProducts) || isNaN(massAll) || massAll === 0) {
    return { calcId: def.id, value: NaN, unit: def.outputUnit ?? '%' };
  }

  const efficiency = (massProducts / massAll) * 100;
  return {
    calcId: def.id,
    value: round(efficiency),
    unit: def.outputUnit ?? '%',
    timestamp: new Date(),
  };
}

/**
 * Phase 4: Real FD Evaluators
 */

function evalFdAssayBasis(
  def: CalcDefinition,
  vars: Record<string, number>
): CalcResult {
  const assayAsIs = vars['assayAsIs']; // %
  const lod = vars['LOD'];             // %

  if (assayAsIs == null || lod == null || isNaN(assayAsIs) || isNaN(lod) || (100 - lod) <= 0) {
    return { calcId: def.id, value: NaN, unit: def.outputUnit ?? '%' };
  }

  const dried = (assayAsIs * 100) / (100 - lod);
  return {
    calcId: def.id,
    value: round(dried),
    unit: def.outputUnit ?? '%',
    timestamp: new Date(),
  };
}

function evalFdOverage(
  def: CalcDefinition,
  vars: Record<string, number>
): CalcResult {
  const labelClaim = vars['labelClaim'];
  const actual = vars['actualAssay'];

  if (!labelClaim || !actual || isNaN(labelClaim) || isNaN(actual) || labelClaim === 0) {
    return { calcId: def.id, value: NaN, unit: def.outputUnit ?? '%' };
  }

  const overage = ((actual - labelClaim) / labelClaim) * 100;
  return {
    calcId: def.id,
    value: round(overage),
    unit: def.outputUnit ?? '%',
    timestamp: new Date(),
  };
}

/**
 * Phase 4: Real ADL Evaluators
 */

function evalAdlLod(
  def: CalcDefinition,
  vars: Record<string, number>
): CalcResult {
  const initial = vars['initialWeight'];
  const finalW = vars['finalWeight'];

  if (!initial || finalW == null || isNaN(initial) || isNaN(finalW) || initial === 0) {
    return { calcId: def.id, value: NaN, unit: def.outputUnit ?? '%' };
  }

  const lod = ((initial - finalW) / initial) * 100;
  return {
    calcId: def.id,
    value: round(lod),
    unit: def.outputUnit ?? '%',
    timestamp: new Date(),
  };
}

function evalAdlVariation(
  def: CalcDefinition,
  vars: Record<string, number>
): CalcResult {
  const v1 = vars['value1'];
  const v2 = vars['value2'];

  if (v1 == null || v2 == null || isNaN(v1) || isNaN(v2) || v1 === 0) {
    return { calcId: def.id, value: NaN, unit: def.outputUnit ?? '%' };
  }

  const variation = ((v2 - v1) / v1) * 100;
  return {
    calcId: def.id,
    value: round(variation),
    unit: def.outputUnit ?? '%',
    timestamp: new Date(),
  };
}

/**
 * Legacy: Resolve input value from manual inputs or BOM (Phase 2 compatibility)
 */
function resolveInputValue(
  input: CalcDefinition['inputs'] extends (infer T)[] ? T : { id: string; source: any },
  experiment: ChemExperimentState,
  manualInputs: Record<string, number>
): number {
  const { source }: any = input;

  switch (source.type) {
    case 'manual':
      return manualInputs[input.id] ?? 0;
    case 'bomTotal':
      return sumBomByRole(experiment.materials, source.role);
    case 'bomField':
      return getBomFieldByRole(experiment.materials, source.role, source.field);
    default:
      console.warn('Unknown input source type:', source);
      return 0;
  }
}

function sumBomByRole(materials: MaterialLine[], role: string): number {
  return materials
    .filter((m) => m.role === role)
    .reduce((sum, m) => sum + (m.quantity || 0), 0);
}

function getBomFieldByRole(
  materials: MaterialLine[],
  role: string,
  field: 'quantity'
): number {
  const material = materials.find((m) => m.role === role);
  if (!material) return 0;

  switch (field) {
    case 'quantity':
      return material.quantity || 0;
    default:
      return 0;
  }
}

function evaluateFormula(
  formula: string,
  variables: Record<string, number>
): number {
  try {
    const varNames = Object.keys(variables);
    const varValues = Object.values(variables);
    const extendedVars = { ...variables, Math };
    const extendedNames = [...varNames, 'Math'];
    const extendedValues = [...varValues, Math];

    const evaluator = new Function(...extendedNames, `return ${formula}`);
    const result = evaluator(...extendedValues);

    if (typeof result !== 'number' || !isFinite(result)) {
      throw new Error('Formula did not return a valid number');
    }

    return result;
  } catch (error) {
    console.error('Formula evaluation error:', error);
    throw new Error(`Invalid formula: ${error}`);
  }
}

/**
 * Create a calculation engine instance
 */
export function createCalculationEngine(): CalculationEngine {
  return {
    evaluate(
      def: CalcDefinition,
      experiment: ChemExperimentState,
      manualInputs: Record<string, number>
    ): CalcResult {
      try {
        // Phase 4: Use evaluator-based system if available
        if (def.evaluator && def.variables) {
          const vars = resolveVariables(def.variables, experiment, manualInputs);

          // Route to appropriate evaluator
          switch (def.evaluator) {
            case 'api.stoichYield':
              return evalApiStoichYield(def, vars);
            case 'api.greenMetric':
              return evalApiGreenMetric(def, vars);
            case 'fd.assayBasis':
              return evalFdAssayBasis(def, vars);
            case 'fd.overage':
              return evalFdOverage(def, vars);
            case 'adl.lod':
              return evalAdlLod(def, vars);
            case 'adl.variation':
              return evalAdlVariation(def, vars);
            default:
              throw new Error(`Unknown evaluator: ${def.evaluator}`);
          }
        }

        // Phase 2: Legacy formula-based system (backward compatibility)
        if (def.formula && def.inputs) {
          const variables: Record<string, number> = {};

          for (const input of def.inputs) {
            if (input !== undefined) {
              const value = resolveInputValue(input, experiment, manualInputs);
              variables[input.id] = value;
            }


          }

          const result = evaluateFormula(def.formula, variables);

          return {
            calcId: def.id,
            value: round(result),
            unit: def.outputUnit,
            timestamp: new Date(),
          };
        }

        throw new Error('Calculation definition must have either evaluator+variables or formula+inputs');
      } catch (error) {
        console.error('Calculation error:', error);
        throw new Error(`Failed to evaluate calculation "${def.name}": ${error}`);
      }
    },
  };
}

/**
 * Validate a formula before using it
 */
export function validateFormula(formula: string): { valid: boolean; error?: string } {
  try {
    const dangerousPatterns = [
      /\bimport\b/,
      /\brequire\b/,
      /\beval\b/,
      /\bFunction\b/,
      /\bwindow\b/,
      /\bdocument\b/,
      /\bprocess\b/,
      /\b__\w+__\b/,
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(formula)) {
        return {
          valid: false,
          error: `Formula contains prohibited pattern: ${pattern}`,
        };
      }
    }

    new Function('x', `return ${formula}`);
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: `Syntax error: ${error}`,
    };
  }
}

/**
 * Helper to format calculation results
 */
export function formatCalcResult(result: CalcResult, decimals: number = 2): string {
  if (isNaN(result.value)) {
    return 'Invalid';
  }
  const valueStr = result.value.toFixed(decimals);
  return result.unit ? `${valueStr} ${result.unit}` : valueStr;
}

/**
 * Export singleton instance for convenience
 */
export const calculationEngine = createCalculationEngine();
